import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Chip, DialogContentText, TextField, Typography } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useListReviewAbuseReportsQuery, useDestroyReviewAbuseReportMutation, usePartialUpdateReviewAbuseReportMutation } from '../features/api/rest/apiReviewsReportsSlice';
import Spinner from './Spinner';

function ReviewReportEdit({ reviewId }) {
  const { ean } = useParams();
  const { t } = useTranslation();
  const { data: fetchedReports } = useListReviewAbuseReportsQuery({ ean, reviewId });
  const [report, setReport] = React.useState(fetchedReports.map((item) => ({ type: item.type, note: item.note, id: item.id })));
  const [destroyReviewReport, { isLoading: deleteLoading }] = useDestroyReviewAbuseReportMutation();
  const [lastEdited, setLastEdited] = React.useState(null);
  const [partialUpdateReviewReport, { isLoading: updateLoading, isSuccess: updateSuccess }] = usePartialUpdateReviewAbuseReportMutation();
  const isLoading = deleteLoading || updateLoading;

  React.useEffect(() => {
    setReport(fetchedReports.map((item) => ({ type: item.type, note: item.note, id: item.id })));
  }, [fetchedReports]);

  const handleNoteChange = (type, newNote) => {
    setReport((prevReports) => prevReports.map((item) => (item.type === type ? { ...item, note: newNote } : item)));
    setLastEdited(null);
  };

  const handleDeleteReport = (id) => {
    destroyReviewReport({ ean, reviewId, id });
  };

  const handleSubmitReportNote = (id, type) => {
    const { note } = report.find((item) => item.type === type);
    partialUpdateReviewReport({ ean, reviewId, id, note });
    setLastEdited(type);
  };

  return (
    <>
      {fetchedReports?.length > 0 && <Typography variant="body3">{t('reports.edit.description')}</Typography>}
      {fetchedReports?.map((fetchedReport) => (
        <Box key={fetchedReport.type} sx={{ backgroundColor: 'background.paper', py: 2, borderRadius: 1 }}>
          <Chip label={t(`reports.types.${fetchedReport.type}`)} color="error" deleteIcon={<DeleteIcon />} onDelete={() => handleDeleteReport(fetchedReport.id)} disabled={isLoading} />
          <TextField
            multiline
            maxRows={3}
            fullWidth
            defaultValue={fetchedReport?.note}
            value={report?.note}
            onChange={(e) => handleNoteChange(fetchedReport.type, e.target.value)}
            disabled={isLoading}
            sx={{ my: 2 }}
            inputProps={{ 'data-testid': `${fetchedReport.type}_noteInput` }}
            FormHelperTextProps={{ sx: { color: 'success.main', position: 'absolute', bottom: -20 } }}
            helperText={lastEdited === fetchedReport.type && updateSuccess && t('reports.edit.messages.success')}
          />
          <Button onClick={() => handleSubmitReportNote(fetchedReport.id, fetchedReport.type)} disabled={isLoading || report?.find((item) => item.type === fetchedReport.type)?.note === fetchedReport?.note}>
            {t('reports.actions.edit')}
          </Button>
        </Box>
      ))}
      {fetchedReports?.length === 0 && <DialogContentText sx={{ mt: 2 }}>{t('reports.edit.noReports')}</DialogContentText>}
      {isLoading && <Spinner removeBox />}
    </>
  );
}

ReviewReportEdit.propTypes = {
  reviewId: PropTypes.number.isRequired,
};

export default ReviewReportEdit;
